/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useDispatch} from "useDispatch";
import React, {useEffect, useState} from "react";
import BookingAction, {PaymentAssert} from "store/booking/BookingAction";
import Loader from "components/Loader";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import {useTranslation} from "react-i18next";
import {currencyNumberFormat, translate} from "utils/utility";
import {AtSymbolIcon, PhoneIcon} from "@heroicons/react/24/outline";
import {useTypedSelector} from "helpers/reducers";
import {Payment} from "types/entities/Payment";
import {PaymentStatus} from "types/enums/PaymentStatus";
import {RouteComponentProps, withRouter} from "react-router-dom";
import {ThunkAction} from "redux-thunk";
import VoucherAction from "store/shop/VoucherAction";
import {HallCode} from "types/enums/HallCode";
import {DEFAULT_BASENAME, DEFAULT_HALL, DEFAULT_HALL_ID, HAS_BASENAME} from "helpers/config";
import {confirmGiftShopArticleItemsCart} from "helpers/actions";
import {toast} from "react-toastify";
import {Ticket} from "types/entities/Ticket";
import ReactGA from "react-ga4";
import TagManager from "react-gtm-module";
import {useCookies} from "utils/hooks/useCookies";
import HallAction from "store/hall/HallAction";

interface Params {
    payment_id: string;
    reference: string;
}

interface BookingPaidProps extends RouteComponentProps<Params> {}

const BookingGiftCardPaid = (props: BookingPaidProps) => {
    const dispatch = useDispatch();
    const [result, setResult] = useState<Payment | null>(null);
    const [assert, setAssert] = useState<PaymentAssert | null>(null);

    const {t} = useTranslation();
    const hall = useTypedSelector(state => state.hallState?.hall) || null;
    const [paymentItems, setPaymentItems] = useState<Ticket[]>([]);

    const itemsCart = useTypedSelector(state => state.shopState.itemsCart);
    const transactionTotal = result?.amount;
    const {cookiesEnabled, showCookiesBanner} = useCookies();

    const fetchPaidGiftCardCart = () => {
        dispatch<ThunkAction<any, any, any, any>>(BookingAction.fetchPaidGiftCardCart(+props.match.params.payment_id, props.match.params.reference)).then(r => {
            setResult(r.payment);
            setAssert(r.assert);

            if (r.payment && r.payment.tickets) {
                const paymentTickets: Ticket[] = r.payment.tickets?.flatMap(ticket => ticket) || [];
                setPaymentItems(paymentTickets);
            }
        });
    };

    const dataLayerItems: {
        item_id: number | null;
        item_name: any;
        price: number | null;
        currency: string;
    }[] = paymentItems.map(item => {
        return {
            item_id: item.article_id, // Change this to the identifier for the item
            item_name: item.article ? translate(item.article.article_name) : "Gift card", // Change this to the name of the item
            price: item.price,
            currency: "CHF", // Change it if the currency is different for the item
        };
    });

    const dataLayer = {
        params: {
            action: "purchase",
            category: "ecommerce",
            label: "Giftcards Paid",
            value: transactionTotal ? transactionTotal / 100 : 0,
            currency: "CHF", // Assuming the currency is USD, change it if it's different
            transaction_id: result?.reservation_id,
            user_id: result?.customer?.id,
            payment_id: result?.id,
            payment_status: result?.status,
            items: dataLayerItems,
        },
    };

    const sendBookingPaidEvent = () => {
        ReactGA.event("purchase", {
            action: "purchase",
            category: "Ecommerce",
            currency: "CHF",
            label: "Giftcards Paid",
            transaction_id: result?.id,
            value: transactionTotal ? transactionTotal / 100 : 0,
            items: dataLayerItems,
        });
    };

    useEffect(() => {
        fetchPaidGiftCardCart();
    }, []);

    useEffect(() => {
        dispatch(VoucherAction.resetVouchers());
        dispatch(VoucherAction.resetAppliedVouchers());
    }, []);

    useEffect(() => {
        if (cookiesEnabled) {
            if (result) {
                if (paymentItems.length !== 0) {
                    sendBookingPaidEvent();
                }
            }
        }
    }, [result, paymentItems]);

    useEffect(() => {
        if (result && paymentItems && paymentItems.length !== 0) {
            TagManager.dataLayer({dataLayer: dataLayer});
        }
    }, [result, paymentItems]);

    const retryHandler = () => {
        if (itemsCart) {
            if (itemsCart.shopArticleItems.length > 0 && itemsCart.shopArticleItemsCart !== null) {
                const url = HAS_BASENAME ? "/" + DEFAULT_BASENAME + "/" : "/";
                dispatch(confirmGiftShopArticleItemsCart(url + "booking/giftcard/paid/", itemsCart.shopArticleItemsCart)).then(r => {});
            } else {
                toast.warning(t("Your shopping cart is empty"));
            }
        }
    };

    const finishHandler = () => {
        props.history.push("/booking");
    };

    useEffect(() => {
        if (!hall) {
            dispatch(HallAction.fetchHall(DEFAULT_HALL_ID)).then(r => {});
        }
    }, []);

    if (!result || !hall) {
        return <Loader />;
    }

    return (
        <div className={"BookingGiftCardPaid w-full flex flex-col justify-start items-center"}>
            <div className={"BookingPaidInner py-3 px-2 sm:py-12 px:12 flex flex-col space-y-2 sm:space-y-8  max-w-2xl"}>
                <div className={"PaymentStatus flex flex-col space-y-2 sm:space-y-8 lg:flex-wrap justify-center items-center px-4 sm:px-12"}>
                    <CardComponent className={"flex-grow text-lg"}>
                        {(result.status === PaymentStatus.Finished ||
                            result.status === PaymentStatus.Authorized ||
                            result.status === PaymentStatus.Captured) && (
                            <h3 className={"text-lg sm:text-3xl font-black text-primary-800"}>{t("Thank you for your purchase!")}</h3>
                        )}
                        {result.status === PaymentStatus.Started && (
                            <h3 className={"text-lg sm:text-3xl font-black text-red-500"}>{t("Something went wrong with your purchase")}</h3>
                        )}
                    </CardComponent>

                    {(result.status === PaymentStatus.Finished || result.status === PaymentStatus.Authorized || result.status === PaymentStatus.Captured) && (
                        <CardComponent className={"flex-grow text-lg"}>
                            <div className={"text-lg"}>
                                <span>{t("Your purchase has been successfully completed")}.</span>&nbsp;
                                <span>{t("Please review the information below")}.</span>
                            </div>
                        </CardComponent>
                    )}
                    {result.status === PaymentStatus.Started && (
                        <CardComponent className={"flex-grow text-lg"}>
                            <div className={"flex flex-col justify-center items-center p-4 rounded-md space-y-2 sm:space-y-8"}>
                                <span>{t("Please retry payment again in a few minutes")}</span>
                            </div>
                        </CardComponent>
                    )}
                </div>

                {(result.status === PaymentStatus.Finished || result.status === PaymentStatus.Authorized || result.status === PaymentStatus.Captured) && (
                    <>
                        <div className={"PaymentGiftCardTickets grid sm:grid-cols-2 gap-4 sm:gap-8 sm:justify-between items-center px-4 sm:px-12"}>
                            {result.tickets &&
                                result.tickets.map((ticket: Ticket) => {
                                    return (
                                        <CardComponent key={ticket.id} className={"flex-grow text-lg"}>
                                            <div className={"flex flex-col justify-center items-center p-4 rounded-md space-y-2 sm:space-y-8"}>
                                                {ticket.price && (
                                                    <div className={"bg-secondary-100 px-2 py-1 rounded-md"}>{currencyNumberFormat(ticket.price * 100)}</div>
                                                )}
                                                <span>{ticket.article ? translate(ticket.article.article_name) : ""}</span>
                                                <img alt={"ticket QR code"} className={"w-[120px] h-[120px]"} src={ticket.qr ? ticket.qr : ""} />
                                                {DEFAULT_HALL !== HallCode.Zwolle && (
                                                    <a
                                                        className={"px-3 py-2 rounded-md bg-cta-800 text-white"}
                                                        download={ticket.giftcard ? ticket.giftcard : ""}
                                                        href={ticket.giftcard ? ticket.giftcard : ""}
                                                        rel="noopener noreferrer"
                                                        target="_blank"
                                                    >
                                                        {t("Download Gift Card")}
                                                    </a>
                                                )}
                                            </div>
                                        </CardComponent>
                                    );
                                })}
                        </div>
                        <div className={"PaymentCustomer flex-grow flex flex-row lg:flex-wrap justify-center items-center px-4 sm:px-12"}>
                            <CardComponent className={"w-full text-lg"}>
                                {result.customer && (
                                    <div className={"flex flex-col space-y-2 mb-4 pb-4"}>
                                        <h4>{t("Contact information that you've entered")}</h4>
                                        <span>
                                            {t("Customer")}:{" "}
                                            <b>
                                                {result.customer.first_name} {result.customer.last_name}
                                            </b>
                                        </span>
                                        <span>
                                            {t("Email")}: <b>{result.customer.email}</b>
                                        </span>
                                        <span>
                                            {t("Phone")}: <b>{result.customer.phone}</b>
                                        </span>
                                    </div>
                                )}
                                <hr />
                                <div>{t("If you have any questions or problems, please contact our customer service directly")}.</div>
                                {hall.contact && (
                                    <div className={"flex flex-row justify-center items-center"}>
                                        <PhoneIcon className={"w-4 h-4 mr-2"} />{" "}
                                        <a href={`tel:${hall.contact}`}>
                                            <b>{hall.contact}</b>
                                        </a>
                                    </div>
                                )}
                                {hall.email && (
                                    <div className={"flex flex-row justify-center items-center"}>
                                        <AtSymbolIcon className={"w-4 h-4 mr-2"} />{" "}
                                        <a href={`mailto:${hall.email}`}>
                                            <b>{hall.email}</b>
                                        </a>
                                    </div>
                                )}
                            </CardComponent>
                        </div>
                        <div className={"PaymentReceipt flex-grow flex flex-row lg:flex-wrap justify-center items-center px-4 sm:px-12"}>
                            <CardComponent className={"w-full text-lg"}>
                                <div className={"flex flex-col space-y-2 mb-4 pb-4 leading-5"}>
                                    <div>{hall.ticket_header}</div>
                                    <div>{hall.address}</div>
                                    <hr />
                                    <h4 className={"font-black"}>{t("Receipt for your purchase")}</h4>
                                    <hr />
                                    <div className={"flex-grow flex flex-col justify-between"}>
                                        <span className={"font-medium mb-2"}>{t("Items bought")}</span>
                                        <div className={"flex flex-col"}>
                                            {result.paymentItems &&
                                                result.paymentItems.map(paymentItem => {
                                                    if (!paymentItem.article) {
                                                        return null;
                                                    }
                                                    return (
                                                        <div key={paymentItem.id} className={"flex-grow flex flex-row justify-between"}>
                                                            <span>
                                                                {translate(paymentItem.article.article_name)} -{" "}
                                                                {paymentItem.article.person_type && t(paymentItem.article.person_type)}
                                                            </span>
                                                            <span>{paymentItem.price ? currencyNumberFormat(paymentItem.price) : ""}</span>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                    <div className={"flex-grow flex flex-row justify-between font-bold"}>
                                        <span>{t("Total price")}</span>
                                        <span>{currencyNumberFormat(result.amount)}</span>
                                    </div>
                                    <hr />
                                    <div>{hall.ticket_qrinfo}</div>
                                    <div>{hall.ticket_footer}</div>
                                </div>
                            </CardComponent>
                        </div>
                    </>
                )}
            </div>

            <div className="flex w-full flex-col max-w-md">
                {result.status === PaymentStatus.Finished && (
                    <button
                        className={
                            "hover:opacity-80 bg-cta-800 text-contrast-800 cursor-pointer hover:no-underline text-base sm:text-xl flex-grow text-center px-4 py-2 sm:px-5 sm:py-3 flex flex-row justify-center rounded-lg items-center"
                        }
                        onClick={finishHandler}
                    >
                        {t("Finish")}
                    </button>
                )}
                {result.status === PaymentStatus.Started && (
                    <div className={"w-full flex flex-row justify-between space-x-4 md:space-x-8 m-auto"}>
                        <button
                            className={
                                "hover:opacity-80 bg-cta-800 text-contrast-800 cursor-pointer hover:no-underline text-base sm:text-xl flex-grow text-center px-4 py-2 sm:px-5 sm:py-3 flex flex-row justify-center rounded-lg items-center"
                            }
                            onClick={retryHandler}
                        >
                            {t("Retry")}
                        </button>
                        <button
                            className={
                                "hover:opacity-80 bg-white text-cta-800 cursor-pointer hover:no-underline text-base sm:text-xl flex-grow text-center px-4 py-2 sm:px-5 sm:py-3 flex flex-row justify-center rounded-lg items-center"
                            }
                            onClick={finishHandler}
                        >
                            {t("Cancel")}
                        </button>
                    </div>
                )}
            </div>

            {/*<Dump data={result} />*/}
            {/*Final @todo - chronological rider with all details about arrival, map, venue, guest host contact, etc..*/}
        </div>
    );
};

export default withRouter(BookingGiftCardPaid);
